import React from "react";
import { useNavigate } from "react-router-dom";
import {
  formatDuration,
  getJobTypeDisplayName,
  getStatusColor,
  type SchedulerRun,
} from "../../services/schedulerAdminService";

interface SchedulerRunDetailsProps {
  run: SchedulerRun;
  onClose: () => void;
}

export function SchedulerRunDetails({ run, onClose }: SchedulerRunDetailsProps) {
  const navigate = useNavigate();
  const statusColor = getStatusColor(run.status);
  const colorClasses = {
    green: "bg-green-500/20 text-green-400 border-green-500/30",
    yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    red: "bg-red-500/20 text-red-400 border-red-500/30",
    gray: "bg-surface-600/20 text-surface-400 border-surface-600/30",
  };

  const formatTime = (dateStr: string | undefined) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return "-";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-surface-100">
                {getJobTypeDisplayName(run.job_type)} Run
              </h2>
              <p className="text-sm text-surface-400">
                {formatTime(run.started_at)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-700/50 transition-colors"
          >
            <svg
              className="w-5 h-5 text-surface-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Status Badge */}
        <div className="mb-6">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${colorClasses[statusColor]}`}
          >
            {run.status === "running" && (
              <span className="w-2 h-2 rounded-full bg-current mr-2 animate-pulse"></span>
            )}
            {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
          </span>
          {run.trigger_type === "manual" && (
            <span className="ml-2 inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
              Manual Trigger
            </span>
          )}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <DetailItem label="Job Type" value={getJobTypeDisplayName(run.job_type)} />
          <DetailItem label="Trigger Type" value={run.trigger_type} />
          <DetailItem label="Started At" value={formatTime(run.started_at)} />
          <DetailItem
            label="Completed At"
            value={run.completed_at ? formatTime(run.completed_at) : "-"}
          />
          <DetailItem label="Duration" value={formatDuration(run.duration_ms)} />
          <DetailItem label="Run ID" value={run.id} mono />
        </div>

        {/* Correlation ID */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-surface-300 mb-2">
            Correlation ID
          </h3>
          <div className="bg-surface-800/50 rounded-lg p-3 border border-surface-700/50">
            <code className="text-sm text-surface-200 break-all">
              {run.correlation_id}
            </code>
          </div>
          <p className="text-xs text-surface-500 mt-1">
            Use this ID to trace related activity logs and API calls
          </p>
        </div>

        {/* Error Message */}
        {run.error_message && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-red-400 mb-2">
              Error Message
            </h3>
            <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
              <pre className="text-sm text-red-300 whitespace-pre-wrap font-mono">
                {run.error_message}
              </pre>
            </div>
          </div>
        )}

        {/* Errors Array (from cronjobs) */}
        {run.errors && run.errors.length > 0 && !run.error_message && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-red-400 mb-2">
              Errors ({run.errors.length})
            </h3>
            <div className="space-y-2">
              {run.errors.map((error, idx) => (
                <div key={idx} className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                  <pre className="text-sm text-red-300 whitespace-pre-wrap font-mono">
                    {error}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {run.summary && Object.keys(run.summary).length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-surface-300 mb-2">
              Run Summary
            </h3>
            <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700/50">
              <SummaryContent summary={run.summary} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-surface-700/50">
          {run.job_type === "execution" && run.status !== "running" && (
            <button
              onClick={() => {
                onClose();
                navigate(`/admin/scheduler/runs/${run.id}/report`);
              }}
              className="btn-primary px-4 py-2 bg-amber-600 hover:bg-amber-500"
            >
              View Full Report
            </button>
          )}
          <button
            onClick={() => {
              navigator.clipboard.writeText(run.correlation_id);
            }}
            className="btn-secondary px-4 py-2"
          >
            Copy Correlation ID
          </button>
          <button onClick={onClose} className="btn-primary px-4 py-2">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-surface-500 mb-1">{label}</dt>
      <dd
        className={`text-sm text-surface-200 ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}

function SummaryContent({ summary }: { summary: Record<string, unknown> }) {
  // Render different summary formats based on content
  const renderValue = (_key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-surface-500">-</span>;
    }

    if (typeof value === "boolean") {
      return (
        <span className={value ? "text-green-400" : "text-red-400"}>
          {value ? "Yes" : "No"}
        </span>
      );
    }

    if (typeof value === "number") {
      return <span className="text-blue-400">{value.toLocaleString()}</span>;
    }

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return <span className="text-surface-500">Empty</span>;
        }
        return (
          <div className="pl-2 border-l border-surface-600/50">
            {value.map((item, idx) => (
              <div key={idx} className="text-xs text-surface-300 py-0.5">
                {typeof item === "object" ? JSON.stringify(item) : String(item)}
              </div>
            ))}
          </div>
        );
      }

      // Nested object
      return (
        <div className="pl-2 border-l border-surface-600/50 mt-1">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="py-0.5">
              <span className="text-xs text-surface-500">{k}: </span>
              {renderValue(k, v)}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-surface-200">{String(value)}</span>;
  };

  // Check for common summary patterns
  const hasOrderInfo =
    "orders_placed" in summary ||
    "orders_combined" in summary ||
    "trades_executed" in summary;
  const hasReconciliationInfo =
    "reconciled" in summary || "reconciliation_details" in summary;
  const hasSyncInfo =
    "accounts_processed" in summary && "accounts_succeeded" in summary && !("positions_captured" in summary);
  const hasSnapshotInfo =
    "positions_captured" in summary || ("accounts_processed" in summary && !hasSyncInfo);
  const hasExecutionInfo =
    "total_strategies" in summary || "total_orders_submitted" in summary;
  const hasPriceRefreshInfo =
    "tickers_total" in summary && "tickers_updated" in summary;
  const hasCacheCompareInfo =
    "tickers_checked" in summary && "days_compared" in summary;

  if (hasCacheCompareInfo) {
    const mismatchTickers = summary.mismatch_tickers as string[] | undefined;
    return (
      <div className="space-y-3">
        <SummarySection title="Cache Comparison Summary">
          <SummaryRow label="Tickers Checked" value={summary.tickers_checked as number} />
          <SummaryRow label="Days Compared" value={summary.days_compared as number} />
          <SummaryRow label="Perfect Matches" value={summary.perfect_matches as number} />
          <SummaryRow label="Match %" value={`${summary.match_pct}%`} />
          <SummaryRow label="Price Differences" value={summary.price_diffs as number} />
          <SummaryRow label="Mismatches" value={summary.mismatches_count as number} />
          <SummaryRow label="Duration" value={`${summary.duration_seconds}s`} />
        </SummarySection>

        <SummarySection title="Coverage">
          <SummaryRow label="RAM Tickers" value={summary.total_ram_tickers as number} />
          <SummaryRow label="Redis Tickers" value={summary.total_redis_tickers as number} />
          <SummaryRow label="RAM Only" value={summary.ram_only_count as number} />
          <SummaryRow label="Redis Only" value={summary.redis_only_count as number} />
          <SummaryRow label="In Redis Not RAM" value={summary.in_redis_not_ram_count as number} />
        </SummarySection>

        {mismatchTickers && mismatchTickers.length > 0 && (
          <SummarySection title="Mismatch Tickers">
            <div className="mt-1">
              <span className="text-xs text-yellow-400">
                {mismatchTickers.join(", ")}
                {(summary.mismatches_count as number) > mismatchTickers.length &&
                  ` (+${(summary.mismatches_count as number) - mismatchTickers.length} more)`}
              </span>
            </div>
          </SummarySection>
        )}

        {(summary.errors_count as number) > 0 && (
          <SummarySection title="Errors">
            <div className="bg-red-500/10 rounded p-2 border border-red-500/20">
              <span className="text-xs text-red-300">{summary.errors_count as number} ticker(s) had errors</span>
            </div>
          </SummarySection>
        )}
      </div>
    );
  }

  if (hasOrderInfo) {
    return (
      <div className="space-y-3">
        <SummarySection title="Execution Summary">
          <SummaryRow
            label="Orders Placed"
            value={summary.orders_placed as number}
          />
          <SummaryRow
            label="Orders Combined"
            value={summary.orders_combined as number}
          />
          <SummaryRow
            label="Trades Executed"
            value={summary.trades_executed as number}
          />
          {summary.total_value !== undefined && (
            <SummaryRow
              label="Total Value"
              value={`$${(summary.total_value as number).toLocaleString()}`}
            />
          )}
        </SummarySection>
        {hasReconciliationInfo && (
          <SummarySection title="Reconciliation">
            <SummaryRow
              label="Reconciled"
              value={(summary.reconciled as boolean) ? "Yes" : "No"}
            />
            {Boolean(summary.reconciliation_details) && (
              <div className="mt-2 text-xs text-surface-400">
                <pre className="whitespace-pre-wrap">
                  {JSON.stringify(summary.reconciliation_details as Record<string, unknown>, null, 2)}
                </pre>
              </div>
            )}
          </SummarySection>
        )}
      </div>
    );
  }

  if (hasExecutionInfo) {
    return (
      <div className="space-y-3">
        <SummarySection title="Execution Summary">
          <SummaryRow label="Accounts Processed" value={summary.accounts_processed as number} />
          <SummaryRow label="Accounts Succeeded" value={summary.accounts_succeeded as number} />
          <SummaryRow label="Accounts Failed" value={summary.accounts_failed as number} />
          <SummaryRow label="Total Strategies" value={summary.total_strategies as number} />
          <SummaryRow label="Orders Submitted" value={summary.total_orders_submitted as number} />
          <SummaryRow label="Orders Filled" value={summary.total_orders_filled as number} />
          <SummaryRow label="Orders Failed" value={summary.total_orders_failed as number} />
        </SummarySection>
      </div>
    );
  }

  if (hasSyncInfo) {
    return (
      <div className="space-y-3">
        <SummarySection title="Sync Summary">
          <SummaryRow label="Accounts Processed" value={summary.accounts_processed as number} />
          <SummaryRow label="Accounts Succeeded" value={summary.accounts_succeeded as number} />
          <SummaryRow label="Accounts Failed" value={summary.accounts_failed as number} />
        </SummarySection>
      </div>
    );
  }

  if (hasSnapshotInfo) {
    return (
      <div className="space-y-3">
        <SummarySection title="Snapshot Summary">
          <SummaryRow
            label="Accounts Processed"
            value={summary.accounts_processed as number}
          />
          <SummaryRow
            label="Positions Captured"
            value={summary.positions_captured as number}
          />
          {summary.total_value !== undefined && (
            <SummaryRow
              label="Total Value"
              value={`$${(summary.total_value as number).toLocaleString()}`}
            />
          )}
        </SummarySection>
      </div>
    );
  }

  if (hasPriceRefreshInfo) {
    const nanTickers = summary.nan_tickers as string[] | undefined;
    const errorMsg = summary.error as string | undefined;
    return (
      <div className="space-y-3">
        <SummarySection title="Price Refresh Summary">
          <SummaryRow label="Total Tickers" value={summary.tickers_total as number} />
          <SummaryRow label="Tickers Updated" value={summary.tickers_updated as number} />
          <SummaryRow label="Batch Download Received" value={summary.batch_download_received as number} />
          <SummaryRow label="No yFinance Data" value={summary.tickers_no_data as number} />
          <SummaryRow label="Skipped (No Cache)" value={summary.tickers_skipped as number} />
          <SummaryRow label="Duration" value={`${summary.duration_seconds}s`} />
        </SummarySection>

        <SummarySection title="Data Quality">
          <SummaryRow label="NaN Issues" value={summary.nan_issues_count as number} />
          {nanTickers && nanTickers.length > 0 && (
            <div className="mt-1">
              <span className="text-xs text-surface-500">Affected tickers: </span>
              <span className="text-xs text-yellow-400">
                {nanTickers.join(", ")}
                {(summary.nan_issues_count as number) > nanTickers.length &&
                  ` (+${(summary.nan_issues_count as number) - nanTickers.length} more)`}
              </span>
            </div>
          )}
        </SummarySection>

        <SummarySection title="Cache Status">
          <div className="flex justify-between text-sm">
            <span className="text-surface-400">RAM Refresh Signaled</span>
            <span className={`font-medium ${summary.ram_refresh_signaled ? "text-green-400" : "text-red-400"}`}>
              {summary.ram_refresh_signaled ? "Yes" : "No"}
            </span>
          </div>
        </SummarySection>

        {errorMsg && (
          <SummarySection title="Error">
            <div className="bg-red-500/10 rounded p-2 border border-red-500/20">
              <span className="text-xs text-red-300 font-mono">{errorMsg}</span>
            </div>
          </SummarySection>
        )}
      </div>
    );
  }

  // Generic rendering for unknown summary structure
  return (
    <div className="space-y-2">
      {Object.entries(summary).map(([key, value]) => (
        <div key={key} className="flex flex-wrap">
          <span className="text-xs text-surface-500 min-w-[120px]">
            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}:
          </span>
          <span className="text-sm">{renderValue(key, value)}</span>
        </div>
      ))}
    </div>
  );
}

function SummarySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-xs font-medium text-surface-400 mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) {
  if (value === undefined) return null;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-surface-400">{label}</span>
      <span className="text-surface-200 font-medium">
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
    </div>
  );
}
